import { useEffect } from 'react'
import { supabase } from '@/lib/supabase/client'

interface UseRealtimeMealNeedsProps {
  tripId: string
  enabled: boolean
  onMealNeedsUpdate: () => void
}

export function useRealtimeMealNeeds({ tripId, enabled, onMealNeedsUpdate }: UseRealtimeMealNeedsProps) {
  useEffect(() => {
    if (!enabled || !tripId) return

    // Subscribe to meal changes
    const mealsChannel = supabase
      .channel(`trip_meals_${tripId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trip_meals',
          filter: `trip_id=eq.${tripId}`
        },
        () => {
          onMealNeedsUpdate()
        }
      )
      .subscribe()

    // Subscribe to meal contributors changes
    const contributorsChannel = supabase
      .channel(`trip_meal_contributors_${tripId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trip_meal_contributors'
        },
        (payload) => {
          // Check if this contributor is related to our trip's meals
          if (payload.new && typeof payload.new === 'object' && 'meal_id' in payload.new) {
            // We need to check if this meal belongs to our trip
            // For now, we'll refresh all meals to be safe
            onMealNeedsUpdate()
          }
        }
      )
      .subscribe()

    // Subscribe to meal opt-outs so headcount updates for chefs
    const optOutsChannel = supabase
      .channel(`trip_meal_opt_outs_${tripId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trip_meal_opt_outs'
        },
        () => {
          onMealNeedsUpdate()
        }
      )
      .subscribe()

    return () => {
      mealsChannel.unsubscribe()
      contributorsChannel.unsubscribe()
      optOutsChannel.unsubscribe()
    }
  }, [tripId, enabled, onMealNeedsUpdate])
}
